import { useCallback, useMemo } from "react";
import Grid from "components/primitives/Grid";
import NoDataWrapper from "components/NoDataWrapper";
import { useRequest } from "hooks";
import useDefinedContext from "hooks/useDefinedContext";
import useWindowDimensions from "hooks/useWindowDimensions";
import type { Leaderboard, LeaderboardBase } from "types";
import LeaderboardControlsContext from "../contexts/LeaderboardControlsContext";
import useLeaderboardsContext from "../hooks/useLeaderboardsContext";
import LeaderboardEditable from "./LeaderboardEditable";
import { calculateLeaderboardSize } from "./LeaderboardTables.utils";
import NoLeaderboardsMessage from "./NoLeaderboardsMessage";
import logEvent from "utils/logEvent";

interface LeaderboardTablesProps {}

function LeaderboardTables(props: LeaderboardTablesProps) {
  const [leaderboards, leaderboardActions] = useLeaderboardsContext();
  const [showCreate, setShowCreate] = useDefinedContext(LeaderboardControlsContext);
  const saveLeaderboard = useRequest(leaderboardActions.update, true);
  const windowDimensions = useWindowDimensions();
  const createLeaderboard = useRequest(leaderboardActions.create, true);

  const handleSaveCurried = useCallback(
    (id: string) => async (leaderboard: LeaderboardBase) => {
      await saveLeaderboard(id, leaderboard);
    },
    [saveLeaderboard]
  );

  // TODO: This should be rethought and refactored.
  /**
   * @description
   * This function is used to cancel the creation of a new leaderboard.
   * Since the leaderboard isn't create before setting some values,
   * we can just set the state to false.
   */
  const onCancelCreate = useCallback(() => {
    setShowCreate(false);
  }, [setShowCreate]);

  const handleCreate = useCallback(
    async (leaderboard: LeaderboardBase) => {
      await createLeaderboard(leaderboard);
      logEvent("LEADERBOARD_CREATE");
      setShowCreate(false);
    },
    [createLeaderboard, setShowCreate]
  );

  const leaderboardWithCreate = useMemo(() => {
    const contactedLeaderboards: (Leaderboard | undefined)[] = [...leaderboards];

    if (showCreate) {
      contactedLeaderboards.push(undefined);
    }

    return contactedLeaderboards;
  }, [leaderboards, showCreate]);

  const lbSize = calculateLeaderboardSize(leaderboardWithCreate.length, windowDimensions.width);

  return (
    <>
      <Grid container spacing={0} height="100%" width="100%" justifyContent="center" m="auto" position="relative">
        <NoDataWrapper
          hasData={Boolean(leaderboardWithCreate.length)}
          fallback={
            !showCreate && (
              <Grid maxWidth={600}>
                <NoLeaderboardsMessage />
              </Grid>
            )
          }
        >
          {leaderboardWithCreate.map((leaderboard, leaderboardIndex) => (
            /**
             * @author Carden
             * The @prop key - `leaderboardIndex` is used here so that
             * the leaderboard holds its state when it transitions from
             * the create state to the edit state.
             *
             * Warning: This will break if the leaderboard is sorted.
             * Traditionally, this should be done with a unique ID.
             */
            <Grid key={leaderboardIndex} size={lbSize.xsWidth} sx={{ height: "100%", padding: 1 }}>
              <LeaderboardEditable
                leaderboard={leaderboard}
                onSubmit={leaderboard ? handleSaveCurried(leaderboard.id) : handleCreate}
                onCancelCreate={onCancelCreate}
                configPanelOpen={typeof leaderboard === "undefined"}
              />
            </Grid>
          ))}
        </NoDataWrapper>
      </Grid>
    </>
  );
}

export default LeaderboardTables;
